import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {connect} from 'react-redux';
import { StyledVendorsTabs } from './StyledVendorsTabs';
import ReloadButton from '../../NetSuiteJournalEntriesPage/ReloadButton';
import TableFiltersRightSideContent from '../../NetSuiteJournalEntriesPage/TableFiltersRightSideContent';
import Tabs from '../../../Tabs';
import VendorsHistoryTab from '../tabs/VendorsHistoryTab';
import VendorsTab from '../tabs/VendorsTab';
import {
  accountingHelpers,
  locationHelpers,
  tabHelpers,
} from '../../../../utils/helpers';
import {accountingActions} from '../../../../state/actions/accounting.actions';
import {useAccountingTabLogic, useTabState} from '../../../../hooks';
import {vendorSourceConstants, vendorSyncStatusConstants} from '../../../../constants';

const tabKeys = {
  ERP_VENDORS: 'erp_vendors',
  NOT_ON_ERP: 'not_on_erp',
  SYNCED: 'synced',
};

const {ERP_VENDORS, NOT_ON_ERP, SYNCED} = tabKeys;

const defaultTabKey = tabKeys.NOT_ON_ERP;

const tabKeysArr = [ERP_VENDORS, NOT_ON_ERP, SYNCED];

const {showAlert} = accountingHelpers;

const VendorsTabs = ({
  dispatch,
  getVendors,
  getVendorsTotals,
  syncVendors,
  updateVendor,
  ...rest
}) => {
  const location = useLocation();
  const [t] = useTranslation(['main', 'accounting']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || defaultTabKey);
  const [total, setTotal] = useState(undefined);
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
    requiredUpdate, setRequiredUpdate,
    selectedRowKeys, setSelectedRowKeys,
  } = useTabState({tabKeys: tabKeysArr});

  const loadTotals = () => {
    getVendorsTotals({
      successCallback: (data) => {
        setTotal({
          [NOT_ON_ERP]: data?.backend_count || 0,
          [ERP_VENDORS]: data?.m3g_netsuite_count || 0,
        });
      },
      errorCallback: () => setTotal(null)
    });
  }

  const loadData = ({
    view,
    query = null,
    initial = false,
    updateRequired = true,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    const finishTableLoading = () => {
      setLoading(prevState => ({...prevState, [view]: false}));
      if (updateRequired) {
        setRequiredUpdate(prevState => ({...prevState, [view]: false}));
      }
    }

    setLoading(prevState => ({...prevState, [view]: true}));

    getVendors({
      query: getQuery(viewQuery, view),
      successCallback: (response) => {
        response.results = response.results.map(accountingHelpers.formatVendor);
        tabHelpers.handleStoreTableDataWithPrev({
          tableData, setTableData, view, response, initial, updateCurrentPage
        });
        finishTableLoading();
        onSuccess?.();
      },
      errorCallback: finishTableLoading
    });
  }

  const {
    handleOnPaginate,
    handleRowSelect,
    handleOnTableChange,
    handleOnFilter,
    onChangeTab,
    updateTab,
  } = useAccountingTabLogic({
    tabKeys,
    tableData,
    setTableData,
    queries,
    setQueries,
    requiredUpdate,
    loadData,
    setActiveTabKey,
    setSelectedRowKeys
  });

  const trans = (key, props) => t(`accounting:${key}`, props);

  const syncEntries = ({ ids, onSuccess }) => {
    accountingHelpers.handleSyncItems({
      ids, loadData, onSuccess, onSync: syncVendors, setSyncButtonLoading, trans
    });
  };

  const handleSync = () => {
    const ids = selectedRowKeys[NOT_ON_ERP] || [];

    syncEntries({
      ids,
      onSuccess: () => {
        setSelectedRowKeys(prevState => ({ ...prevState, [NOT_ON_ERP]: [] }));
        loadData({
          view: NOT_ON_ERP, updateCurrentPage: true, onSuccess: () => updateTab(SYNCED)
        });
        loadTotals();
      }
    });
  };

  const handleResync = () => {
    syncEntries({
      ids: selectedRowKeys?.[SYNCED],
      onSuccess: () => setSelectedRowKeys(prevState => ({ ...prevState, [SYNCED]: [] }))
    });
  };

  const handleClickOnSyncButton = () => {
    const actions = {
      [NOT_ON_ERP]: handleSync,
      [SYNCED]: handleResync,
    };

    actions[activeTabKey]?.(activeTabKey);
  }

  const getTableFiltersRightSideContent = (view) => {
    if (view === ERP_VENDORS) return ;
    const availableSync = Boolean(selectedRowKeys?.[view].length);

    return (
      <TableFiltersRightSideContent
        syncButtonProps={{ disabled: !availableSync, loading: syncButtonLoading, onClick: availableSync && handleClickOnSyncButton }}
      />
    )
  }

  useEffect(() => {
    loadData({
      initial: true,
      view: tabKeys[activeTabKey.toUpperCase()]
    });
    loadTotals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getLabel = ({ key, totalKey }) =>
    tabHelpers.getLabelWithOptionalTotal({ key, total, totalKey, t: (key) => trans(`vendors.${key}`) });

  const getQuery = (query, view) => {
    let statusesEnum = {
      [NOT_ON_ERP]: { source: vendorSourceConstants.BACKEND, sync_status: vendorSyncStatusConstants.NONE },
      [SYNCED]: { source: undefined, sync_status: vendorSyncStatusConstants.ALL },
      [ERP_VENDORS]: { source: vendorSourceConstants.M3G_NETSUITE, sync_status: undefined },
    };
    return {
      ...(statusesEnum[view] || {}),
      ...query,
    }
  }

  const handleRowUpdate = ({id, data, view, sendRequest = true}) => {
    if (sendRequest) {
      updateVendor({
        id,
        data,
        successCallback: (response) => {
          const updatedData = accountingHelpers.formatVendor(response);
          const updatedRows = tableData[view].rows.map(row => row.id === id ? updatedData : row);
          setTableData(prevState => ({
            ...prevState,
            [view]: {
              ...tableData[view],
              rows: updatedRows
            }
          }));
        },
        errorCallback: () => {
          showAlert('error', trans('validation.failedToUpdateVendor'));
        }
      })
    } else {
      const updatedRows = tableData[view].rows.map(row => row.id === id ? {...row, ...data} : row);
      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...tableData[view],
          rows: updatedRows
        }
      }));
    }
  }

  const handleRefresh = () => loadData({ initial: true, view: tabKeys[activeTabKey.toUpperCase()] });

  const getTabProps = (key) => {
    let tableProps = {};
    let tableFiltersProps = {};

    if (key === SYNCED) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          onChange: (rows) => handleRowSelect(key, rows),
          selectedRowKeys: selectedRowKeys[key] || [],
          syncStatus: vendorSyncStatusConstants.ERROR
        })
      }
      tableFiltersProps = { leftSideContent: <ReloadButton onClick={handleRefresh} /> };
    } else if (key === NOT_ON_ERP) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          onChange: (rows) => handleRowSelect(key, rows),
          selectedRowKeys: selectedRowKeys[key],
        }),
      }
    }

    return accountingHelpers.getTabProps({
      key,
      totalKey: key,
      tableData,
      loading,
      handleOnFilter,
      handleRowUpdate,
      handleOnTableChange,
      handleOnPaginate,
      getTableFiltersRightSideContent,
      total,
      tableFiltersProps,
      tableProps
    });
  }

  const tabs = [
    {
      key: NOT_ON_ERP,
      label: getLabel({ key: NOT_ON_ERP, totalKey: NOT_ON_ERP }),
      children: <VendorsTab {...getTabProps(NOT_ON_ERP)} variant='backend' />
    },
    {
      key: SYNCED,
      label: getLabel({ key: SYNCED }),
      children: <VendorsHistoryTab {...getTabProps(SYNCED)} />
    },
    {
      key: ERP_VENDORS,
      label: getLabel({ key: ERP_VENDORS, totalKey: ERP_VENDORS }),
      children: <VendorsTab {...getTabProps(ERP_VENDORS)} variant='erp' />
    },
  ];

  return (
    <StyledVendorsTabs>
      <Tabs
        {...rest}
        activeKey={activeTabKey}
        items={tabs}
        onChange={onChangeTab}
        type='card'
      />
    </StyledVendorsTabs>
  );
}

const mapDispatchToProps = {
  getVendors: accountingActions.getVendors,
  getVendorsTotals: accountingActions.getVendorsTotals,
  syncVendors: accountingActions.syncVendors,
  updateVendor: accountingActions.updateVendor,
}

export default connect(null, mapDispatchToProps)(VendorsTabs);
